if (!customElements.get('variant-cards')) {
  customElements.define(
    'variant-cards',
    class VariantCards extends HTMLElement {
      constructor() {
        super();
        this.variantFormId = null;
        this.sectionId = this.dataset.section;
        this.updateUrl = this.dataset.updateUrl !== 'false';
      }

      connectedCallback() {
        // Find the cards and variant input
        this.cards = this.querySelectorAll('.pricing-card');
        this.variantInput = null;
        
        // Wait for DOM to be ready before finding form elements
        setTimeout(() => {
          this.initializeCards();
          
          // Check for variant in URL and select that card if it exists
          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get('variant');
          
          if (variantId) {
            const cardForVariant = Array.from(this.cards).find(card => 
              card.dataset.variantId === variantId
            );
            
            if (cardForVariant) {
              this.handleCardClick({ currentTarget: cardForVariant });
            }
          }
        }, 0);
      }

      initializeCards() {
        // Find the form and variant input
        this.productForm = document.querySelector(`#product-form-${this.sectionId}`);
        if (!this.productForm) {
          console.warn('VariantCards: Product form not found', this.sectionId);
          return;
        }
        
        this.variantInput = this.productForm.querySelector('input[name="id"]');
        if (!this.variantInput) {
          console.warn('VariantCards: Variant input not found in form');
          return;
        }

        // Add click and keyboard listeners to cards with ARIA attributes
        this.cards.forEach(card => {
          // Add role and accessibility attributes
          card.setAttribute('role', 'button');
          card.setAttribute('tabindex', '0');
          card.setAttribute('aria-pressed', card.classList.contains('active') ? 'true' : 'false');
          
          // Add event listeners
          card.addEventListener('click', this.handleCardClick.bind(this));
          card.addEventListener('keydown', this.handleCardKeyDown.bind(this));
        });

        // Set initial variant if none is active
        const activeCard = this.querySelector('.pricing-card.active');
        if (!activeCard && this.cards.length > 0) {
          this.cards[0].classList.add('active');
          this.cards[0].setAttribute('aria-pressed', 'true');
          this.updateVariantInput(this.cards[0].dataset.variantId);
        }
      }
      
      handleCardKeyDown(event) {
        // Handle Enter or Space key
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handleCardClick(event);
        }
      }

      handleCardClick(event) {
        const clickedCard = event.currentTarget;
        const variantId = clickedCard.dataset.variantId;
        
        // Don't proceed if already active
        if (clickedCard.classList.contains('active')) return;
        
        // Update active state and ARIA attributes
        this.cards.forEach(card => {
          const isActive = card === clickedCard;
          card.classList.toggle('active', isActive);
          card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        
        // Update variant input and notify the product form
        this.updateVariantInput(variantId);
        
        // Get the selected variant data
        const selectedVariantData = this.getSelectedVariantData(variantId);
        
        // Publish event for other components
        this.publishVariantChangeEvent(selectedVariantData);
        
        // Update URL if needed
        if (this.updateUrl) {
          this.updateBrowserHistory(variantId);
        }
      }

      updateVariantInput(variantId) {
        if (!this.variantInput) return;
        
        this.variantInput.value = variantId;
        
        // Dispatch change event to notify form
        const changeEvent = new Event('change', { bubbles: true });
        this.variantInput.dispatchEvent(changeEvent);
      }

      getSelectedVariantData(variantId) {
        // Get all product variants from the data-selected-variant script tag
        const variantScript = this.querySelector('[data-selected-variant]');
        if (!variantScript) return null;
        
        try {
          const selectedVariant = JSON.parse(variantScript.textContent);
          const allVariants = this.getAllVariants();
          
          // Find the selected variant
          return allVariants.find(variant => variant.id.toString() === variantId.toString()) || selectedVariant;
        } catch (e) {
          console.error('Error parsing variant data:', e);
          return null;
        }
      }
      
      getAllVariants() {
        // This is a simplified implementation since we don't have access to all variants
        // In a real implementation, this would fetch all variants from a data attribute or the API
        return this.cards.map(card => ({
          id: card.dataset.variantId,
          price: card.dataset.price,
        }));
      }

      publishVariantChangeEvent(variantData) {
        if (!variantData) return;
        
        // Publish event similar to variant-selects.js for compatibility
        if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              variant: variantData,
            }
          });
        }
        
        // Also dispatch standard event for broader compatibility
        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: variantData
            }
          })
        );
      }

      updateBrowserHistory(variantId) {
        if (!this.updateUrl) return;
        
        const productUrl = this.dataset.url || window.location.pathname;
        const searchParams = new URLSearchParams(window.location.search);
        
        searchParams.set('variant', variantId);
        window.history.replaceState({ path: window.location.pathname }, '', `${productUrl}?${searchParams.toString()}`);
      }
    }
  );
}
